/**
 * SearchBarContext - Global state management for search functionality
 * 
 * This context manages:
 * 1. Search parameters (dates, guests, bedrooms) that persist across pages
 * 2. Hero search bar visibility tracking using IntersectionObserver
 * 3. Synchronization between main SearchBar and compact header SearchBar
 */

import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';

/**
 * Search parameters interface
 * All search criteria are stored here and shared across the application
 */
interface SearchParams {
  dateRange: [Date | null, Date | null];
  guests: { adults: number; children: number; infants: number };
  bedrooms: number;
  isFlexible: boolean;
  flexibleDays: number; // 0-5 days flexibility
}

/**
 * Current property interface for gallery mode
 */
interface CurrentProperty {
  id: string;
  title: string;
  price: number;
  images: string[];
  bedrooms: number;
  bathrooms: number;
  guests: number;
  location: string;
}

/**
 * Context type definition
 */
interface SearchBarContextType {
  isHeroSearchVisible: boolean;
  setIsHeroSearchVisible: (visible: boolean) => void;
  heroSearchRef: React.RefObject<HTMLDivElement>;
  setHeroSearchElement: (element: HTMLDivElement | null) => void;
  searchParams: SearchParams;
  updateSearchParams: (params: Partial<SearchParams>) => void;
  currentProperty: CurrentProperty | null;
  setCurrentProperty: (property: CurrentProperty | null) => void;
}

const SearchBarContext = createContext<SearchBarContextType | undefined>(undefined);

// Function to get default search parameters with dynamic dates
const getDefaultSearchParams = (): SearchParams => {
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Set to start of day
  
  const checkIn = new Date(today);
  checkIn.setDate(checkIn.getDate() + 1); // Tomorrow
  
  const checkOut = new Date(checkIn);
  checkOut.setDate(checkOut.getDate() + 7); // 7 nights later
  
  return {
    dateRange: [checkIn, checkOut],
    guests: { adults: 2, children: 2, infants: 0 },
    bedrooms: 2,
    isFlexible: false,
    flexibleDays: 2, // Default to 2 days when flexibility is enabled
  };
};

export const useSearchBar = () => {
  const context = useContext(SearchBarContext);
  if (!context) {
    throw new Error('useSearchBar must be used within SearchBarProvider');
  }
  return context;
};

export const SearchBarProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isHeroSearchVisible, setIsHeroSearchVisible] = useState(true);
  const [currentProperty, setCurrentPropertyState] = useState<CurrentProperty | null>(null);
  
  // Wrap setCurrentProperty in useCallback to prevent infinite re-renders
  const setCurrentProperty = useCallback((property: CurrentProperty | null) => {
    setCurrentPropertyState(property);
  }, []);
  const [searchParams, setSearchParams] = useState<SearchParams>(() => {
    // Get default values first
    let params = getDefaultSearchParams();
    
    // Load from localStorage on initial render
    const saved = localStorage.getItem('searchParams');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        // Convert date strings back to Date objects
        if (parsed.dateRange) {
          parsed.dateRange = parsed.dateRange.map((date: string | null) => 
            date ? new Date(date) : null
          );
        }
        // If saved params exist, merge with defaults
        params = { ...params, ...parsed };
        
        // Validate that dates are not in the past
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        if (params.dateRange[0] && params.dateRange[0] < today) {
          // If check-in is in the past, reset to defaults
          const defaults = getDefaultSearchParams();
          params.dateRange = defaults.dateRange;
        }
      } catch (e) {
        console.error('Failed to parse saved search params:', e);
      }
    }
    
    return params;
  });
  const heroSearchRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Update search params
  const updateSearchParams = useCallback((params: Partial<SearchParams>) => {
    setSearchParams(prev => {
      const newParams = { ...prev, ...params };
      // Save to localStorage
      localStorage.setItem('searchParams', JSON.stringify({
        ...newParams,
        // Convert dates to strings for storage
        dateRange: newParams.dateRange.map(date => date?.toISOString() || null)
      }));
      return newParams;
    });
  }, []);

  // Callback ref to handle element mounting
  const setHeroSearchElement = useCallback((element: HTMLDivElement | null) => {
    heroSearchRef.current = element;
    
    // Clean up previous observer
    if (observerRef.current) {
      observerRef.current.disconnect();
    }
    
    if (element) {
      observerRef.current = new IntersectionObserver(
        ([entry]) => {
          setIsHeroSearchVisible(entry.isIntersecting);
        },
        {
          threshold: 0.1, // Trigger when 10% of element is visible
          rootMargin: '-80px 0px 0px 0px' // Consider it hidden when 80px from top (header height)
        }
      );
      observerRef.current.observe(element);
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  return (
    <SearchBarContext.Provider value={{
      isHeroSearchVisible,
      setIsHeroSearchVisible,
      heroSearchRef,
      setHeroSearchElement,
      searchParams,
      updateSearchParams,
      currentProperty,
      setCurrentProperty
    }}>
      {children}
    </SearchBarContext.Provider>
  );
};
